// src/Components/dashboard/OverviewDashboard.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ComposedChart, AreaChart, Area, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import {
  Users, TrendingUp, TrendingDown, FileText, Heart,
  ArrowUpRight, Download, RefreshCw, Activity
} from 'lucide-react';
import { dashboardAPI } from '../../services/api';
import { createDashboardRealtimeClient } from '../../services/analyticsRealtime';
import { useTimeRange } from '../layout/DashboardLayout';

const Skeleton = ({ className = '' }) => (
  <div className={`animate-pulse bg-gray-200 dark:bg-gray-700 rounded-lg ${className}`} />
);

const KPISkeleton = () => (
  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
    <div className="flex justify-between">
      <div className="space-y-2 flex-1">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-3 w-20" />
      </div>
      <Skeleton className="h-12 w-12 rounded-lg" />
    </div>
    <Skeleton className="h-5 w-28 mt-3" />
  </div>
);

export default function OverviewDashboard() {
  const navigate          = useNavigate();
  const { timeRange }     = useTimeRange();
  const realtimeClientRef = useRef(null);

  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [stats,        setStats]        = useState(null);
  const [growthTrend,  setGrowthTrend]  = useState([]);
  const [contentMix,   setContentMix]   = useState([]);
  const [hourlyData,   setHourlyData]   = useState(null);
  const [topCreators,  setTopCreators]  = useState(null);
  const [trends,       setTrends]       = useState({});
  const [lastUpdated,  setLastUpdated]  = useState(null);
  const [realtime,     setRealtime]     = useState(false);

  const applyDashboardSnapshot = useCallback((snapshot) => {
    const overview  = snapshot?.overview  || {};
    const users     = snapshot?.users     || {};
    const content   = snapshot?.content   || {};
    const hourly    = snapshot?.hourlyEngagement || [];
    const creators  = snapshot?.topCreators || [];
    const trendSrc  = overview.trends || snapshot?.trends || {};

    // ── User count: try every possible field the backend might return ──────────
    // Backend sends registeredAccounts (auth.users) + profiledUsers (directus_users)
    const registeredAccounts =
      overview.registeredAccounts ??
      users.registeredAccounts    ??
      overview.totalUsers         ??
      users.totalUsers            ??
      0;

    const profiledUsers =
      overview.profiledUsers      ??
      users.profiledUsers         ??
      overview.totalProfiledUsers ??
      users.totalProfiledUsers    ??
      // If backend only returns one count, use it
      registeredAccounts          ??
      0;

    const activeUsers =
      users.activeUsers   ??
      overview.activeUsers ??
      users.activeNow     ??
      0;

    // ── Posts & engagement ─────────────────────────────────────────────────────
    const totalPosts      = content.totalPosts      ?? overview.totalPosts      ?? 0;
    const publishedPosts  = content.publishedPosts  ?? overview.publishedPosts  ?? 0;
    const draftPosts      = content.draftPosts      ?? overview.draftPosts      ?? 0;
    // Likes: try every alias the backend might use
    const totalLikes      = content.totalLikes      ?? overview.totalLikes      ??
                            content.totalReactions  ?? overview.totalReactions  ?? 0;
    const totalComments   = content.totalComments   ?? overview.totalComments   ?? 0;
    const totalEngagement = content.totalEngagement ?? overview.totalEngagement ??
                            (totalLikes + totalComments);
    const engagementRate  = overview.engagementRate ?? content.engagementRate   ??
                            (totalPosts > 0 ? Math.round((totalEngagement / totalPosts) * 100) : 0);
    const avgEngagement   = overview.avgLikesPerPost ?? content.avgEngagementPerPost ??
                            (totalPosts > 0 ? Math.round(totalEngagement / totalPosts) : 0);

    setStats({
      registeredAccounts,
      profiledUsers,
      totalUsers: registeredAccounts,
      activeNow:  users.activeNow ?? overview.activeNow ?? activeUsers,
      totalPosts,
      publishedPosts,
      draftPosts,
      totalLikes,
      totalComments,
      totalEngagement,
      engagementRate,
      avgLikesPerPost: avgEngagement,
    });

    // ── Trends ─────────────────────────────────────────────────────────────────
    const parseTrend = (v) => {
      if (typeof v === 'number' && isFinite(v)) return v;
      if (typeof v === 'string') {
        const n = Number(v.replace('%', '').trim());
        return isFinite(n) ? n : null;
      }
      // Backend also returns trend objects: { percentage, direction, current, previous }
      if (typeof v === 'object' && v !== null && 'percentage' in v) return v.percentage;
      return null;
    };
    const fmtTrend = (v) => {
      const n = parseTrend(v);
      if (n == null) return null;
      return `${Math.abs(n).toFixed(1).replace(/\.0$/, '')}%`;
    };
    const isUp = (v, explicitBool) => {
      if (typeof explicitBool === 'boolean') return explicitBool;
      const n = parseTrend(v);
      if (n != null) return n >= 0;
      if (typeof v === 'object' && v !== null && 'direction' in v) return v.direction === 'up';
      return true;
    };

    const postTrendVal   = trendSrc.postGrowth      ?? trendSrc.totalPosts;
    const engTrendVal    = trendSrc.engagementChange ?? trendSrc.totalEngagement;
    const userTrendVal   = trendSrc.userGrowth       ?? trendSrc.totalUsers;

    setTrends({
      postGrowth:       fmtTrend(postTrendVal),
      engagementChange: fmtTrend(engTrendVal),
      userGrowth:       fmtTrend(userTrendVal),
      isPostUp:         isUp(postTrendVal,  trendSrc.isPostGrowthUp),
      isEngagementUp:   isUp(engTrendVal,   trendSrc.isEngagementUp),
      isUserUp:         isUp(userTrendVal,  trendSrc.isUserGrowthUp),
      hasTrends:        !!(fmtTrend(postTrendVal) || fmtTrend(engTrendVal) || fmtTrend(userTrendVal)),
    });

    // ── Growth trend chart ─────────────────────────────────────────────────────
    // Backend sends platformGrowthTrends (preferred) with label/date/posts/engagement
    // or growthTrends with month/posts
    const rawTrend =
      overview.platformGrowthTrends ||
      overview.growthTrends         ||
      users.userGrowth              ||
      users.growthOverTime          ||
      [];

    setGrowthTrend(rawTrend.map(t => ({
      // "day" is the key used by the XAxis dataKey — map every possible label field
      day:        t.label || t.month || t.date?.substring(0, 10) || t.start?.substring(0, 7) || '',
      posts:      t.posts      || t.newUsers     || 0,
      published:  t.published  || t.posts        || 0,
      comments:   t.comments   || 0,
      likes:      t.likes      || 0,
      engagement: t.engagement || 0,
      users:      t.users      || t.cumulativeUsers || 0,
    })).filter(t => t.day !== '')); // drop empty buckets

    // ── Content mix pie ────────────────────────────────────────────────────────
    setContentMix([
      { type: 'Published', value: publishedPosts,  color: '#10b981' },
      { type: 'Draft',     value: draftPosts,      color: '#f59e0b' },
      { type: 'Likes',     value: totalLikes,      color: '#ec4899' },
      { type: 'Comments',  value: totalComments,   color: '#3b82f6' },
    ]);

    setHourlyData({ hourlyEngagement: hourly, peakHour: snapshot?.peakHour || null });
    setTopCreators({ topCreators: creators, totalCreators: creators.length });
    setLastUpdated(new Date());
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await dashboardAPI.getDashboardSnapshot(timeRange, 5);
      console.log('🍀 snapshot:', res);
      if (res?.data) applyDashboardSnapshot(res.data);
      else if (!res?.success) setError('API returned no data. Check backend logs.');
    } catch (err) {
      setError(err.message || err.toString());
    } finally {
      setLoading(false);
    }
  }, [applyDashboardSnapshot, timeRange]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    realtimeClientRef.current?.disconnect();

    const client = createDashboardRealtimeClient({
      range: timeRange,
      onSnapshot: (payload) => {
        if (payload?.data) { applyDashboardSnapshot(payload.data); setRealtime(true); }
      },
      onUpdate: (payload) => { if (payload?.data) applyDashboardSnapshot(payload.data); },
      onError: () => setRealtime(false),
    });

    realtimeClientRef.current = client;
    client.requestSnapshot();
    return () => {
      client.disconnect();
      if (realtimeClientRef.current === client) {
        realtimeClientRef.current = null;
      }
    };
  }, [applyDashboardSnapshot, timeRange]);

  const fmt = (n) => {
    if (n == null || n === '') return '0';
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
    return String(n);
  };

  const downloadCSV = () => {
    if (!stats) return;
    const rows = [
      ['Beetroot Analytics – Overview', new Date().toLocaleString(), timeRange],
      [],
      ['Metric', 'Value'],
      ['Registered Accounts', stats.registeredAccounts],
      ['Profiled Users',      stats.profiledUsers],
      ['Active Now',          stats.activeNow],
      ['Total Posts',         stats.totalPosts],
      ['Published Posts',     stats.publishedPosts],
      ['Total Likes',         stats.totalLikes],
      ['Total Comments',      stats.totalComments],
      ['Total Engagement',    stats.totalEngagement],
      ['Engagement Rate',     `${stats.engagementRate}%`],
    ].map(r => r.join(',')).join('\n');
    const a = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(new Blob([rows], { type: 'text/csv' })),
      download: `overview-${timeRange}-${new Date().toISOString().split('T')[0]}.csv`,
    });
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const TrendBadge = ({ value, isUp }) => {
    if (!value) return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-400 rounded-full text-xs">N/A</span>
    );
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
        isUp ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
      }`}>
        {isUp ? <TrendingUp size={11}/> : <TrendingDown size={11}/>} {value}
      </span>
    );
  };

  const KPICard = ({ title, value, sub, icon: Icon, gradient, trend, trendUp, onClick }) => (
    <button onClick={onClick}
      className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-5 hover:shadow-xl transition-all duration-200 hover:-translate-y-0.5 hover:border-red-300 border-2 border-transparent cursor-pointer text-left w-full group">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <p className="text-xs text-gray-500 font-medium">{title}</p>
          {loading ? <Skeleton className="h-8 w-28 mt-1" /> : (
            <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">{value}</h3>
          )}
          <p className="text-xs text-gray-400 mt-0.5">{sub}</p>
        </div>
        <div className={`${gradient} p-2.5 rounded-lg group-hover:scale-110 transition-transform flex-shrink-0`}>
          <Icon className="text-white" size={22} />
        </div>
      </div>
      {trend
        ? <TrendBadge value={trend} isUp={trendUp} />
        : !loading && (
          <div className="flex items-center gap-1 text-xs text-red-500 opacity-0 group-hover:opacity-100 transition">
            <span>View details</span><ArrowUpRight size={12}/>
          </div>
        )
      }
    </button>
  );

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Overview</h1>
          <p className="text-sm text-gray-500">
            Real-time insights · {timeRange}
            {lastUpdated && <span className="ml-2 text-xs text-gray-400">Updated {lastUpdated.toLocaleTimeString()}</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            realtime ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
          }`}>
            {realtime ? 'Realtime On' : 'Realtime Off'}
          </span>
          <button onClick={fetchAll}
            className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-xl text-sm hover:border-red-400 transition">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button onClick={downloadCSV}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-xl text-sm hover:bg-red-700 transition">
            <Download size={14} /> Export CSV
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl text-sm">
          <strong>Error:</strong> {error}
          <button onClick={fetchAll} className="ml-3 underline">Retry</button>
        </div>
      )}

      {/* KPI Cards */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {Array(4).fill(0).map((_, i) => <KPISkeleton key={i}/>)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          <KPICard
            title="Registered Accounts"
            value={fmt(stats?.registeredAccounts)}
            sub={
              stats?.activeNow != null
                ? `${fmt(stats.activeNow)} active · ${fmt(stats?.profiledUsers)} profiled`
                : `${fmt(stats?.profiledUsers)} profiled`
            }
            icon={Users}
            gradient="bg-gradient-to-br from-blue-500 to-blue-600"
            trend={trends.userGrowth}
            trendUp={trends.isUserUp}
            onClick={() => navigate('/dashboard/user-analytics')}
          />
          <KPICard
            title="Total Posts"
            value={fmt(stats?.totalPosts)}
            sub={`${fmt(stats?.publishedPosts)} published`}
            icon={FileText}
            gradient="bg-gradient-to-br from-purple-500 to-purple-600"
            trend={trends.postGrowth}
            trendUp={trends.isPostUp}
            onClick={() => navigate('/dashboard/content-analytics')}
          />
          <KPICard
            title="Total Engagement"
            value={fmt(stats?.totalEngagement)}
            sub={`${stats?.engagementRate || 0}% rate · ${fmt(stats?.totalLikes)} likes`}
            icon={Heart}
            gradient="bg-gradient-to-br from-pink-500 to-red-600"
            trend={trends.engagementChange}
            trendUp={trends.isEngagementUp}
            onClick={() => navigate('/dashboard/content-analytics')}
          />
          <KPICard
            title="Avg Engagement"
            value={fmt(stats?.avgLikesPerPost)}
            sub="Per post"
            icon={TrendingUp}
            gradient="bg-gradient-to-br from-green-500 to-emerald-600"
            onClick={() => navigate('/dashboard/content-analytics')}
          />
        </div>
      )}

      {/* Stat strip */}
      {!loading && stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { label: 'Total Likes',    value: fmt(stats.totalLikes),    color: 'from-blue-50 to-cyan-50 border-blue-100',      text: 'text-blue-600',   sub: 'All reactions'       },
            { label: 'Total Comments', value: fmt(stats.totalComments), color: 'from-purple-50 to-pink-50 border-purple-100',  text: 'text-purple-600', sub: 'User discussions'    },
            { label: 'Profiled Users', value: fmt(stats.profiledUsers), color: 'from-green-50 to-emerald-50 border-green-100', text: 'text-green-600',  sub: 'Users with profiles' },
          ].map(k => (
            <div key={k.label} className={`bg-gradient-to-br ${k.color} rounded-xl p-5 border`}>
              <p className="text-xs font-medium text-gray-500">{k.label}</p>
              <p className={`text-2xl font-bold mt-1 ${k.text}`}>{k.value}</p>
              <p className="text-xs text-gray-400 mt-0.5">{k.sub}</p>
            </div>
          ))}
        </div>
      )}

      {/* Growth chart + pie */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4 dark:text-gray-100">Platform Growth Trends</h3>
          {loading ? <Skeleton className="h-64 w-full" /> : growthTrend.length === 0 ? (
            <div className="h-64 flex flex-col items-center justify-center text-gray-300">
              <TrendingUp size={36} className="mb-2" />
              <p className="text-sm">No growth data for this period</p>
              <p className="text-xs mt-1 text-gray-400">Try "This year" for a wider range</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={growthTrend}>
                <defs>
                  <linearGradient id="postsGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#3b82f6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                <XAxis dataKey="day" tick={{ fontSize: 11 }}/>
                <YAxis yAxisId="left"  tick={{ fontSize: 11 }}/>
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }}/>
                <Tooltip
                  contentStyle={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 8, fontSize: 12, color: '#f9fafb' }}
                  labelStyle={{ color: '#9ca3af' }}
                />
                <Legend/>
                <Area  yAxisId="left"  type="monotone" dataKey="posts"      fill="url(#postsGrad)" stroke="#3b82f6" strokeWidth={2} name="Posts"/>
                <Area  yAxisId="left"  type="monotone" dataKey="users"      fill="none"            stroke="#8b5cf6" strokeWidth={1.5} strokeDasharray="4 3" name="New Users" dot={false}/>
                <Line  yAxisId="right" type="monotone" dataKey="comments"   stroke="#10b981"       strokeWidth={1.5} name="Comments" dot={false}/>
                <Line  yAxisId="right" type="monotone" dataKey="engagement" stroke="#f59e0b"       strokeWidth={1.5} name="Engagement" dot={false}/>
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4 dark:text-gray-100">Content Distribution</h3>
          {loading ? <Skeleton className="h-48 w-full" /> : contentMix.every(c => c.value === 0) ? (
            <div className="h-48 flex items-center justify-center text-gray-300">
              <p className="text-sm">No data yet</p>
            </div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={190}>
                <PieChart>
                  <Pie data={contentMix} cx="50%" cy="50%" outerRadius={70} dataKey="value"
                    label={e => {
                      const total = contentMix.reduce((s, c) => s + c.value, 0);
                      return e.value > 0 ? `${Math.round((e.value / Math.max(total, 1)) * 100)}%` : '';
                    }}>
                    {contentMix.map((e, i) => <Cell key={i} fill={e.color}/>)}
                  </Pie>
                  <Tooltip/>
                </PieChart>
              </ResponsiveContainer>
              <div className="grid grid-cols-2 gap-1.5 mt-2">
                {contentMix.map(c => (
                  <div key={c.type} className="flex items-center gap-1.5 text-xs">
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: c.color }}/>
                    <span className="text-gray-600 dark:text-gray-400 truncate">{c.type}:</span>
                    <span className="font-medium">{fmt(c.value)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Hourly engagement */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold dark:text-gray-100">24-Hour Engagement Pattern</h3>
            {hourlyData?.peakHour && (
              <p className="text-xs text-gray-400 mt-0.5">
                Peak: {hourlyData.peakHour.hour} · {fmt(hourlyData.peakHour.engagement)} activities
              </p>
            )}
          </div>
        </div>
        {loading ? <Skeleton className="h-48 w-full"/> : hourlyData?.hourlyEngagement?.length > 0 ? (
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={hourlyData.hourlyEngagement}>
              <defs>
                <linearGradient id="hourGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#8b5cf6" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
              <XAxis dataKey="hour" tick={{ fontSize: 10 }}/>
              <YAxis tick={{ fontSize: 11 }}/>
              <Tooltip
                contentStyle={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 8, fontSize: 12, color: '#f9fafb' }}
              />
              <Area type="monotone" dataKey="engagement" stroke="#8b5cf6" fill="url(#hourGrad)" name="Engagement"/>
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-48 flex flex-col items-center justify-center text-gray-300">
            <Activity size={32} className="mb-2"/>
            <p className="text-sm">No hourly data yet</p>
          </div>
        )}
      </div>

      {/* Top creators */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-lg font-semibold dark:text-gray-100">Top Content Creators</h3>
            <p className="text-xs text-gray-400">{topCreators?.totalCreators || 0} total creators</p>
          </div>
          <button onClick={() => navigate('/dashboard/content-analytics?view=creators')}
            className="text-sm text-red-500 hover:underline font-medium">View All →</button>
        </div>

        {loading ? (
          <div className="space-y-3">{Array(4).fill(0).map((_, i) => <Skeleton key={i} className="h-14 w-full"/>)}</div>
        ) : !topCreators?.topCreators?.length ? (
          <div className="text-center py-10">
            <Users size={32} className="text-gray-200 mx-auto mb-2"/>
            <p className="text-gray-400 text-sm">No creator data yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {topCreators.topCreators.map((c, i) => (
              <button key={c.authorId}
                onClick={() => navigate(`/dashboard/content-analytics?creator=${c.authorId}`)}
                className="w-full flex items-center gap-4 p-3.5 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 border border-gray-100 dark:border-gray-700 hover:border-red-200 text-left group transition">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0 ${
                  i===0?'bg-yellow-400 text-yellow-900':i===1?'bg-gray-300 text-gray-700':i===2?'bg-orange-400 text-orange-900':'bg-gray-100 text-gray-600'
                }`}>#{i+1}</div>
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                  {(c.displayName || c.authorId || '?').substring(0,2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-gray-800 dark:text-gray-100 truncate">
                    {c.displayName || `Author ${c.authorId?.substring(0,16)}`}
                  </p>
                  <p className="text-xs text-gray-400">{c.posts} posts · {fmt(c.totalLikes)} likes · {fmt(c.totalComments)} comments</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="font-bold text-red-600">{fmt(c.engagement)}</p>
                  <p className="text-xs text-gray-400">engagement</p>
                </div>
                <span className="text-gray-200 group-hover:text-red-400 transition">→</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}